/** Hook-uri TanStack Query pentru server state. */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import * as ep from './endpoints';

export const qk = {
  me: ['me'] as const,
  sessions: ['sessions'] as const,
  balance: (page?: number) => ['balance', page ?? 1] as const,
  catalog: ['catalog'] as const,
  orders: (status?: string) => ['orders', status ?? 'all'] as const,
  billing: ['billing'] as const,
  business: ['business'] as const,
  templates: (cat?: string) => ['templates', cat ?? 'all'] as const,
  voices: ['voices'] as const,
  characters: (idsKey?: string) => ['characters', idsKey ?? 'all'] as const,
  music: (mood?: string) => ['music', mood ?? 'all'] as const,
  clips: (status?: string) => ['clips', status ?? 'all'] as const,
  clip: (id: string) => ['clip', id] as const,
  shares: (clipId: string) => ['shares', clipId] as const,
  files: (kind?: string) => ['files', kind ?? 'all'] as const,
  products: ['products'] as const,
  product: (id: string) => ['product', id] as const,
  campaigns: (status?: string) => ['campaigns', status ?? 'all'] as const,
  notifications: ['notifications'] as const,
  notificationsUnread: ['notifications', 'unread'] as const,
  support: ['support'] as const,
  supportThread: (id: string) => ['support', id] as const,
  ultra: ['ultra'] as const,
  ultraJob: (id: string) => ['ultra', id] as const,
  ultraMessages: (id: string) => ['ultra', id, 'messages'] as const,
};

export const useMe = () =>
  useQuery({ queryKey: qk.me, queryFn: ep.getMe, retry: false, staleTime: 60_000 });
export const useSessions = () => useQuery({ queryKey: qk.sessions, queryFn: ep.getSessions });
export const useBalance = (page = 1) =>
  useQuery({ queryKey: qk.balance(page), queryFn: () => ep.getBalance(page) });

export const useCatalog = () =>
  useQuery({ queryKey: qk.catalog, queryFn: ep.getCatalog, staleTime: 30_000 });
export const useOrders = (status?: string) =>
  useQuery({ queryKey: qk.orders(status), queryFn: () => ep.getMyOrders({ status }) });
export const useBillingProfiles = () => useQuery({ queryKey: qk.billing, queryFn: ep.getBillingProfiles });
export const useBusinessProfile = () => useQuery({ queryKey: qk.business, queryFn: ep.getBusinessProfile });

export const useTemplates = (cat?: string) =>
  useQuery({ queryKey: qk.templates(cat), queryFn: () => ep.getTemplates(cat) });
export const useVoices = () => useQuery({ queryKey: qk.voices, queryFn: ep.getVoices });
/** Personaje active; `ids` restrânge la personajele șablonului (dacă e setat). */
export const useCharacters = (ids?: string[]) => {
  const key = ids && ids.length > 0 ? ids.slice().sort().join(',') : 'all';
  return useQuery({
    queryKey: qk.characters(key),
    queryFn: () => ep.getCharacters(ids && ids.length > 0 ? ids : undefined),
    staleTime: 30_000,
  });
};
export const useMusic = (mood?: string) =>
  useQuery({ queryKey: qk.music(mood), queryFn: () => ep.getMusic(mood) });

export const useClips = (status?: string) =>
  useQuery({ queryKey: qk.clips(status), queryFn: () => ep.getClips(status ? { status } : undefined) });
export const useClip = (id: string | undefined, refetchInterval?: number | false) =>
  useQuery({
    queryKey: qk.clip(id ?? ''),
    queryFn: () => ep.getClip(id as string),
    enabled: Boolean(id),
    refetchInterval,
  });

export const useShareLinks = (clipId: string | undefined) =>
  useQuery({
    queryKey: qk.shares(clipId ?? ''),
    queryFn: () => ep.getShareLinks(clipId as string),
    enabled: Boolean(clipId),
  });

export const useFiles = (kind?: string) =>
  useQuery({ queryKey: qk.files(kind), queryFn: () => ep.getFiles(kind ? { kind } : undefined) });
export const useProducts = () => useQuery({ queryKey: qk.products, queryFn: () => ep.getProducts() });
export const useProduct = (id: string | undefined) =>
  useQuery({ queryKey: qk.product(id ?? ''), queryFn: () => ep.getProduct(id as string), enabled: Boolean(id) });
export const useCampaigns = (status?: string) =>
  useQuery({ queryKey: qk.campaigns(status), queryFn: () => ep.getCampaigns(status ? { status } : undefined) });

export const useNotifications = () =>
  useQuery({ queryKey: qk.notifications, queryFn: () => ep.getNotifications() });
export const useSupportThreads = () => useQuery({ queryKey: qk.support, queryFn: ep.getSupportThreads });
export const useSupportThread = (id: string | undefined) =>
  useQuery({
    queryKey: qk.supportThread(id ?? ''),
    queryFn: () => ep.getSupportThread(id as string),
    enabled: Boolean(id),
  });

export const useUltraJobs = () => useQuery({ queryKey: qk.ultra, queryFn: ep.getUltraJobs });
export const useUltraJob = (id: string | undefined) =>
  useQuery({ queryKey: qk.ultraJob(id ?? ''), queryFn: () => ep.getUltraJob(id as string), enabled: Boolean(id) });
export const useUltraMessages = (id: string | undefined) =>
  useQuery({
    queryKey: qk.ultraMessages(id ?? ''),
    queryFn: () => ep.getUltraMessages(id as string),
    enabled: Boolean(id),
  });

/** Invalidare rapidă a mai multor chei. */
export function useInvalidate() {
  const qc = useQueryClient();
  return (keys: ReadonlyArray<readonly unknown[]>) =>
    keys.forEach((k) => qc.invalidateQueries({ queryKey: k as unknown[] }));
}

export { useMutation };
